import type {
  CreateTaskInput,
  Task,
  TaskRunWithTask,
  UpdateTaskInput,
} from "schemas/tasks";

type RequestOptions = {
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  query?: Record<string, string | number | undefined>;
  body?: unknown;
};

export function createTasksClient({
  request,
}: {
  request: <ResponseType>(options: RequestOptions) => Promise<ResponseType>;
}) {
  return {
    find: async (input: { projectId: string }): Promise<Task[]> => {
      const result = await request<{ tasks: Task[] }>({
        method: "GET",
        path: "/api/v1/tasks",
        query: { projectId: input.projectId },
      });
      return result.tasks;
    },
    findRuns: async (input: {
      projectId: string;
      limit?: number;
    }): Promise<TaskRunWithTask[]> => {
      const result = await request<{ runs: TaskRunWithTask[] }>({
        method: "GET",
        path: "/api/v1/tasks/runs",
        query: { projectId: input.projectId, limit: input.limit },
      });
      return result.runs;
    },
    create: async (input: CreateTaskInput): Promise<Task> => {
      const result = await request<{ task: Task }>({
        method: "POST",
        path: "/api/v1/tasks",
        body: input,
      });
      return result.task;
    },
    update: async ({
      input: taskInput,
      taskId,
    }: {
      taskId: string;
      input: UpdateTaskInput;
    }): Promise<Task> => {
      const result = await request<{ task: Task }>({
        method: "PUT",
        path: `/api/v1/tasks/${encodeURIComponent(taskId)}`,
        body: taskInput,
      });
      return result.task;
    },
    runNow: async (input: {
      taskId: string;
    }): Promise<{ queued: boolean; run: TaskRunWithTask; task: Task }> => {
      const result = await request<{
        queued: boolean;
        run: TaskRunWithTask;
        task: Task;
      }>({
        method: "POST",
        path: `/api/v1/tasks/${encodeURIComponent(input.taskId)}/run`,
      });
      return result;
    },
    delete: async (input: {
      taskId: string;
    }): Promise<{ deleted: true; task: Task }> => {
      return await request<{ deleted: true; task: Task }>({
        method: "DELETE",
        path: `/api/v1/tasks/${encodeURIComponent(input.taskId)}`,
      });
    },
  };
}

export type TasksClient = ReturnType<typeof createTasksClient>;
